import { useState } from "react";
import {
  Archive,
  CirclePlay,
  FileText,
  RefreshCw,
  XCircle,
} from "lucide-react";

import { ShareablePreview } from "@/core/components/shared/preview-action";
import { createPreviewActions } from "@/core/components/shared/preview-action/create-preview-actions";
import { Button } from "@/core/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCancelPayrollBatch } from "@/features/payroll/api/use-cancel-payroll-batch";
import { useDownloadPayrollBatchStatement } from "@/features/payroll/api/use-download-payroll-batch-statement";
import { useDownloadPayrollBatchStatements } from "@/features/payroll/api/use-download-payroll-batch-statements";
import { useLaunchPayrollBatch } from "@/features/payroll/api/use-launch-payroll-batch";
import { useRefreshPayrollBatch } from "@/features/payroll/api/use-refresh-payroll-batch";
import type { PayrollBatchDto } from "@/features/payroll/api/dto";
import { canSimulatePayrollBatch } from "@/features/payroll/components/can-simulate-payroll-batch";
import { PayrollSimulatePreview } from "@/features/payroll/components/payroll-simulate-preview";

type PendingDialog = "simulate" | "launch" | "cancel" | null;

/**
 * Orchestration conditionnelle des actions sur une paie — rendu guard-first
 * selon `batch.status` :
 *
 * - `DRAFT` / `SIMULATED`  → Simuler s'il existe au moins une ligne
 *                           `VALID` ou `PENDING` (`canSimulatePayrollBatch`)
 *                           + Annuler. `PENDING` suffit.
 * - `SIMULATED`           → Lancer (idempotent, section fees + total via
 *                           `ShareablePreview`, mention « irréversible »)
 *                           + Annuler, si `canLaunch` et solde suffisant.
 * - `PROCESSING`          → Rafraîchir (lecture technique, pas de preview).
 * - `COMPLETED` /         → Relevé global PDF + Archive ZIP (téléchargements).
 *   `PARTIALLY_COMPLETED` /
 *   `FAILED`
 *
 * Les boutons ne sont jamais exécutés au simple clic : chaque mutation passe
 * par un `ShareablePreview` ou `MutationConfirmPreview`. `launch` est le seul
 * endpoint idempotent de la ressource — rejeu avec la même clé = pas de
 * double débit.
 */
export function PayrollBatchActions({ batch }: { batch: PayrollBatchDto }) {
  const { t } = useTypedTranslation();
  const tPayroll = (key: string) => t(key as TFunctionType);

  const [dialog, setDialog] = useState<PendingDialog>(null);

  const launch = useLaunchPayrollBatch(batch.id);
  const cancel = useCancelPayrollBatch(batch.id);
  const refresh = useRefreshPayrollBatch(batch.id);
  const downloadStatement = useDownloadPayrollBatchStatement();
  const downloadZip = useDownloadPayrollBatchStatements();

  const canEdit = batch.status === "DRAFT" || batch.status === "SIMULATED";
  const canSimulate = canSimulatePayrollBatch(batch);
  const canLaunch =
    batch.status === "SIMULATED" && batch.canLaunch && batch.sufficientBalance;
  const canCancel = canEdit;
  const canRefresh = batch.status === "PROCESSING";
  const hasOutput =
    batch.status === "COMPLETED" ||
    batch.status === "PARTIALLY_COMPLETED" ||
    batch.status === "FAILED";

  if (!canSimulate && !canLaunch && !canCancel && !canRefresh && !hasOutput) {
    return null;
  }

  const closeDialog = () => setDialog(null);

  const launchPreviewData = {
    label: batch.label,
    lineCount: batch.lineCount,
    paidCount: batch.paidCount,
    disbursementReference: batch.disbursementReference,
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {t("payroll.actions.section.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {canSimulate && (
            <Button variant="outline" onClick={() => setDialog("simulate")}>
              <CirclePlay className="mr-2 h-4 w-4" />
              {t("payroll.actions.simulate.cta")}
            </Button>
          )}
          {canLaunch && (
            <Button onClick={() => setDialog("launch")}>
              <CirclePlay className="mr-2 h-4 w-4" />
              {t("payroll.actions.launch.cta")}
            </Button>
          )}
          {canCancel && (
            <Button variant="destructive" onClick={() => setDialog("cancel")}>
              <XCircle className="mr-2 h-4 w-4" />
              {t("payroll.actions.cancel.cta")}
            </Button>
          )}
          {canRefresh && (
            <Button
              variant="outline"
              disabled={refresh.isPending}
              onClick={() =>
                refresh.mutate(undefined, {
                  onSuccess: () =>
                    notifSilence(t("payroll.actions.refresh.feedback")),
                  onError: (error) =>
                    notifSilenceError(apiErrorHandler(error) || error.message),
                })
              }
            >
              {refresh.isPending ? (
                <ButtonSpinner />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {t("payroll.actions.refresh.cta")}
            </Button>
          )}
          {hasOutput && (
            <>
              <Button
                variant="outline"
                disabled={downloadStatement.isPending}
                onClick={() =>
                  downloadStatement.mutate(
                    { batchId: batch.id },
                    {
                      onError: (error) =>
                        notifSilenceError(
                          apiErrorHandler(error) || error.message
                        ),
                    }
                  )
                }
              >
                {downloadStatement.isPending ? (
                  <ButtonSpinner />
                ) : (
                  <FileText className="mr-2 h-4 w-4" />
                )}
                {t("payroll.actions.statement.cta")}
              </Button>
              <Button
                variant="outline"
                disabled={downloadZip.isPending}
                onClick={() =>
                  downloadZip.mutate(
                    { batchId: batch.id },
                    {
                      onError: (error) =>
                        notifSilenceError(
                          apiErrorHandler(error) || error.message
                        ),
                    }
                  )
                }
              >
                {downloadZip.isPending ? (
                  <ButtonSpinner />
                ) : (
                  <Archive className="mr-2 h-4 w-4" />
                )}
                {t("payroll.actions.statements.cta")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Simuler : sans frais à ce stade (devis calculé par la simulation). */}
      <PayrollSimulatePreview
        batchId={batch.id}
        label={batch.label}
        lineCount={batch.lineCount}
        open={dialog === "simulate"}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      />

      {/* Lancer : section frais + total depuis la simulation. Irréversible.
          Monté seulement à l'ouverture : `defaultOpen` n'est lu qu'une fois. */}
      {dialog === "launch" ? (
      <ShareablePreview
        defaultOpen
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        data={launchPreviewData}
        title={t("payroll.actions.launch.previewTitle")}
        description={t("payroll.actions.launch.previewDescription")}
        fees={[
          {
            label: t("payroll.actions.launch.fees.gross"),
            amount: batch.grossAmountMinor,
            currency: batch.currency,
          },
          {
            label: t("payroll.actions.launch.fees.fees"),
            amount: batch.feeAmountMinor,
            currency: batch.currency,
          },
        ]}
        total={{
          label: t("payroll.actions.launch.fees.total"),
          amount: batch.debitAmountMinor,
          currency: batch.currency,
        }}
        actions={createPreviewActions({
          onConfirm: () =>
            launch.mutate(undefined, {
              onSuccess: () => {
                notifSilence(t("payroll.actions.launch.feedback"));
                closeDialog();
              },
              onError: (error) =>
                notifSilenceError(apiErrorHandler(error) || error.message),
            }),
          onCancel: closeDialog,
          t: tPayroll,
          confirmLoading: launch.isPending,
          withIndicatorOnConfirm: true,
          withPulseOnConfirm: true,
        })}
      />
      ) : null}

      {/* Annuler : opération sans mouvement d'argent, confirmation simple. */}
      {dialog === "cancel" ? (
      <ShareablePreview
        defaultOpen
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
        data={{ label: batch.label, lineCount: batch.lineCount }}
        title={t("payroll.actions.cancel.previewTitle")}
        description={t("payroll.actions.cancel.previewDescription")}
        actions={createPreviewActions({
          onConfirm: () =>
            cancel.mutate(undefined, {
              onSuccess: () => {
                notifSilence(t("payroll.actions.cancel.feedback"));
                closeDialog();
              },
              onError: (error) =>
                notifSilenceError(apiErrorHandler(error) || error.message),
            }),
          onCancel: closeDialog,
          labels: { confirm: t("payroll.actions.cancel.confirm") },
          t: tPayroll,
          confirmLoading: cancel.isPending,
        })}
      />
      ) : null}
    </>
  );
}
